import { API_URL } from '../config/api';

const looksLikeHtml = (value) => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim().toLowerCase();
  return trimmed.startsWith('<!doctype')
    || trimmed.startsWith('<html')
    || trimmed.includes('<html')
    || trimmed.includes('502 - web server')
    || trimmed.includes('503 -')
    || trimmed.includes('504 -');
};

const looksLikeProxyBlock = (status, body = '') => {
  if (status === 407) return true;
  const text = String(body || '').toLowerCase();
  return (
    text.includes('proxy')
    || text.includes('blocked by')
    || text.includes('access denied')
    || text.includes('forbidden by')
    || text.includes('content filter')
    || text.includes('web filter')
    || text.includes('fortigate')
    || text.includes('zscaler')
    || text.includes('bluecoat')
    || text.includes('palo alto')
    || text.includes('captive portal')
  );
};

const fetchWithTimeout = async (url, ms, options = {}) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, {
      cache: 'no-store',
      signal: controller.signal,
      ...options,
    });
  } finally {
    clearTimeout(timer);
  }
};

export const checkInternetConnection = async () => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return false;
  }
  const probes = [
    { url: 'https://www.gstatic.com/generate_204', mode: 'no-cors' },
    { url: 'https://cloudflare.com/cdn-cgi/trace', mode: 'no-cors' },
    { url: `${API_URL}/auth/check-username`, mode: 'cors' },
  ];
  for (const probe of probes) {
    try {
      await fetchWithTimeout(probe.url, 4000, { mode: probe.mode });
      return true;
    } catch {
      // try next probe
    }
  }
  return false;
};

const probeWorkloopEndpoint = async (path) => {
  try {
    const response = await fetchWithTimeout(`${API_URL}${path}`, 6000);
    const body = await response.text();
    return { ok: true, status: response.status, body };
  } catch (error) {
    return { ok: false, status: null, body: '', error };
  }
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const runLoginConnectivityChecks = async (onUpdate) => {
  const report = (partial) => {
    onUpdate?.(partial);
  };

  report({ internet: 'checking', organization: 'pending', service: 'pending' });
  await sleep(350);
  const internetUp = await checkInternetConnection();
  report({
    internet: internetUp ? 'ok' : 'fail',
    internetDetail: internetUp ? null : 'No internet connection',
  });
  await sleep(450);

  report({ organization: 'checking' });
  await sleep(200);
  const orgProbe = await probeWorkloopEndpoint('/auth/check-organization');
  const orgBlocked = orgProbe.ok && looksLikeProxyBlock(orgProbe.status, orgProbe.body);
  report({
    organization: orgBlocked ? 'fail' : 'ok',
    organizationDetail: orgBlocked
      ? 'Your organization proxy may be blocking Workloop'
      : null,
  });
  await sleep(450);

  report({ service: 'checking' });
  await sleep(200);
  const serviceProbe = await probeWorkloopEndpoint('/auth/check-username');
  const serviceHtml = looksLikeHtml(serviceProbe.body);
  const serviceUp = serviceProbe.ok
    && !serviceHtml
    && serviceProbe.status !== 502
    && serviceProbe.status !== 503
    && serviceProbe.status !== 504
    && serviceProbe.status !== 407;
  report({
    service: serviceUp ? 'ok' : 'fail',
    serviceDetail: serviceUp ? null : 'Workloop service is unreachable',
  });

  return {
    internet: internetUp ? 'ok' : 'fail',
    organization: orgBlocked ? 'fail' : 'ok',
    service: serviceUp ? 'ok' : 'fail',
  };
};

export const describeLoginConnectivityFailure = (results = {}) => {
  if (results.internet === 'fail') {
    return {
      title: 'No internet connection',
      message: 'Your device is offline. Check Wi‑Fi or mobile data, then try signing in again.',
    };
  }
  if (results.organization === 'fail') {
    return {
      title: 'Organization proxy is blocking Workloop',
      message: 'Your office network or proxy appears to be blocking access. Try another network, or ask IT to allow workloop.chat.',
    };
  }
  if (results.service === 'fail') {
    return {
      title: 'Workloop service is down',
      message: 'Internet is working, but the Workloop server did not respond. Please wait a moment and try again.',
    };
  }
  return {
    title: 'Sign-in could not be completed',
    message: 'Something went wrong while reaching Workloop. Please try once again.',
  };
};

